/**
 * @fileoverview Matter Creation Hook - Handles the step 0→1 matter creation boundary.
 *
 * Encapsulates the logic for creating a new matter and its associated audit record
 * when transitioning from the Basics step to the Case Info step.
 *
 * Creates:
 * - Matter with CASE_INGESTION status
 * - Primary client with first/last name
 * - Case ingestion audit record for section tracking
 */
import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import type { NavigateFunction } from 'react-router-dom';
import { toast } from 'sonner';

import { useCreateCaseIngestionMutation } from '@/services/api/caseIngestionService';
import {
  useCreateMatterMutation,
  usePatchMatterClientMutation,
} from '@/services/api/mattersService';
import { MatterStatus } from '@/services/types/client-intake-types';
import { selectUser } from '@/state/selectors/root-selectors';

import { CREATION_STEP_COUNT } from '../constants';
import { GATE_TASKS_DEFAULT } from '../schemas';
import type { CreateMatterFormValues } from '../types';

interface UseCreateMatterFlowCallbacks {
  setMatterId: (id: string) => void;
  navigate: NavigateFunction;
  setCurrentStepIndex: (index: number) => void;
  /** Mark as hydrated to prevent API refetch from overwriting form values */
  markAsHydrated: () => void;
}

/**
 * Encapsulates the matter creation + case ingestion audit creation logic
 * used at the step 0→1 boundary of the unified flow.
 *
 * Only sends basics fields — detail fields are deferred to the Case Info PATCH.
 */
export const useMatterCreation = (
  creationForm: UseFormReturn<CreateMatterFormValues>,
  callbacks: UseCreateMatterFlowCallbacks,
) => {
  const user = useSelector(selectUser);
  const [createMatter, { isLoading: isCreatingMatter }] =
    useCreateMatterMutation();
  const [patchClient] = usePatchMatterClientMutation();
  const [createCaseIngestion, { isLoading: isCreatingAudit }] =
    useCreateCaseIngestionMutation();

  const isCreating = isCreatingMatter || isCreatingAudit;

  const handleCreateMatter = useCallback(async (): Promise<{ matterId: string; clientId: string | undefined }> => {
    if (!user) {
      toast.error('User must be logged in to create a matter');
      throw new Error('User must be logged in');
    }

    const data = creationForm.getValues();

    if (!data.matter_type) {
      toast.error('Case type is required');
      throw new Error('Case type is required');
    }

    // Concatenate first + last name for matter.name
    const matterName = `${data.first_name} ${data.last_name}`.trim();

    // Send Step 0 (Basics) fields — contact_client_directly and other
    // Case Info fields are sent via PATCH in Step 1
    const matterResult = await createMatter({
      firm: data.firm.id,
      creator: user.id,
      name: matterName,
      status: MatterStatus.CASE_INGESTION,
      matter_type: data.matter_type,
      clients: [{
        first_name: data.first_name,
        last_name: data.last_name,
        phone_numbers: [],
      }],
      // Always send task_config with the actual checkbox value
      task_config: {
        gate_tasks_during_initial_review: data.gate_tasks_during_initial_review ?? GATE_TASKS_DEFAULT,
      },
    }).unwrap();

    const newMatterId = matterResult.matter.id;
    const newClientId = matterResult.matter.clients?.[0]?.id;

    // Patch client with first_name and last_name (create endpoint doesn't persist these)
    if (newClientId) {
      await patchClient({
        matter_id: newMatterId,
        matter_client_id: newClientId,
        patch: {
          first_name: data.first_name,
          last_name: data.last_name,
        },
      }).unwrap();
    }

    await createCaseIngestion({ matterId: newMatterId }).unwrap();

    toast.success(
      `Matter "${matterResult.matter.name}" created successfully`,
    );

    // Mark as hydrated BEFORE setting matterId to prevent API refetch from overwriting form values
    callbacks.markAsHydrated();
    callbacks.setMatterId(newMatterId);
    callbacks.navigate(`/matters/${newMatterId}/ingestion`, { replace: true });
    callbacks.setCurrentStepIndex(CREATION_STEP_COUNT);

    return { matterId: newMatterId, clientId: newClientId };
  }, [user, creationForm, createMatter, patchClient, createCaseIngestion, callbacks]);

  return { handleCreateMatter, isCreating };
};
